import collect from "collect.js";
import { Currency } from "@daalder/types";

export function useFormatsMoney() {
    function formatMoney(
        value: number,
        currency: string | Currency = { symbol: "€", code: "EUR" },
        dashed: boolean = true,
        withCurrency: boolean = null,
    ) {
        let currencySymbol: string;
        let currencyCode: string;

        // Get the currency symbol and code
        if (typeof currency === "object") {
            currencySymbol = currency.symbol;
            currencyCode = currency.code;
        } else {
            currencySymbol = currency;
            currencyCode = currency;
        }

        // Get the global show currency sign setting
        const showCurrencySign: boolean = withCurrency ?? window.daalder_show_currency_sign ?? true;

        // Get the Daalder locale
        let locale = window.wordpress_locale.replace("_", "-");
        let moneyParts: { type: string; value: string }[] = [];

        // If a valid currencyCode was passed
        if (currencyCode.length === 3) {
            try {
                // Format the price and get it as an array (of parts)
                moneyParts = new Intl.NumberFormat(locale, {
                    style: "currency",
                    currency: currencyCode,
                    //@ts-ignore Experimental, not yet in TS declaration
                    currencyDisplay: "narrowSymbol",
                }).formatToParts(value);
            } catch (err) {}
        } else {
            try {
                // Fallback to nb-NB formatting
                moneyParts = new Intl.NumberFormat("nb-NB", {
                    style: "currency",
                    currency: currencyCode,
                    //@ts-ignore Experimental, not yet in TS declaration
                    currencyDisplay: "narrowSymbol",
                }).formatToParts(value);
            } catch (err) {}
        }

        for (const moneyPart of moneyParts) {
            // Remove the currency when needed
            if (!showCurrencySign && moneyPart.type == "currency") {
                moneyPart.value = "";
            }
            // Replace the thousands seperator with the Wordpress preference
            if (moneyPart.type == "group" && moneyPart.value != " ") {
                moneyPart.value = window.thousands_seperator;
            }
            // Replace the decimal seperator with the Wordpress preference
            if (moneyPart.type == "decimal") {
                moneyPart.value = window.decimal_seperator;
            }
        }

        // Get (potential) trailing '00'
        let fraction = collect(moneyParts).where("type", "fraction").firstWhere("value", "00");

        // Dash ,- the price
        if (dashed && fraction) {
            fraction.value = "-";
        }

        // Implode the edited moneyParts array and return it
        return collect(moneyParts)
            .pluck("value")
            .implode("")
            .replace(/(?:^(?:&nbsp;)+)|(?:(?:&nbsp;)+$)/g, "")
            .trim();
    }

    function formatMoneyWithoutCurrency(
        value: number,
        currency: string | Currency = { symbol: "€", code: "EUR" },
        dashed: boolean = true,
    ) {
        return formatMoney(value, currency, dashed, false);
    }

    function getAmountIntegerPart(value: number) {
        let float = value * 1;
        let money = float
            .toFixed(2)
            .replace(/(\d)(?=(\d{3})+\.)/g, "$1")
            .split(".")[0];

        if (money.length > 3) {
            money = money.slice(0, money.length - 3) + window.thousands_seperator + money.slice(money.length - 3);
        }

        return money;
    }

    function getAmountDecimalPart(value: number) {
        let float = value * 1;
        return float
            .toFixed(2)
            .replace(/(\d)(?=(\d{3})+\.)/g, "$1")
            .split(".")[1];
    }

    function getOptionLabel(option: { name: string; fetched_price: string }) {
        let price = parseFloat(option.fetched_price);
        if (price !== 0) {
            return option.name + " (" + (price < 0 ? "-" : "+") + " " + formatMoney(price) + ")";
        }

        return option.name;
    }

    function isCurrencyBefore(currency: string | Currency) {
        if (typeof currency === "object") {
            currency = currency.symbol;
        }

        if (currency === "€") {
            currency = "EUR";
        }

        let locale = window.wordpress_locale.replace("_", "-");
        if (currency && currency.length === 3) {
            try {
                let formatter = new Intl.NumberFormat(locale, {
                    style: "currency",
                    currency: currency,
                    //@ts-ignore Experimental, not yet in TS declaration
                    currencyDisplay: "code",
                });
                let parts = formatter.formatToParts(99); // number doesn't matter here
                let currencyIndex = parts.findIndex((part) => part.value === currency);
                return currencyIndex === 0;
            } catch (err) {}
        }

        return true;
    }

    return {
        formatMoney,
        formatMoneyWithoutCurrency,
        getAmountIntegerPart,
        getAmountDecimalPart,
        getOptionLabel,
        isCurrencyBefore,
    };
}
